import { IconButton, notify, usePromptModal } from '@affine/component';
import {
  composeBlobNameFromRename,
  splitBlobNameForRename,
} from '@affine/core/utils/blob-display-name';
import { I18n } from '@affine/i18n';
import {
  ArrowDownSmallIcon,
  ArrowUpSmallIcon,
  AutoHeightIcon,
  CloseIcon,
  CommentIcon,
  DownloadIcon,
  EditIcon,
  EmailIcon,
  MoreHorizontalIcon,
  OpenInNewIcon,
  PrinterIcon,
  ReauthenticationIcon,
  SaveIcon,
  SearchIcon,
  ZoomDownIcon,
  ZoomUpIcon,
} from '@blocksuite/icons/rc';
import clsx from 'clsx';
import type { CSSProperties, RefObject } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

import { getSignedPdfCopyLockedLabel } from './pdf/pdf-signed-version-labels';
import type {
  AttachmentTextEditorState,
  AttachmentViewerSource,
} from './types';
import { download } from './utils';
import * as styles from './viewer.css';

const PDF_TITLEBAR_COMPACT_ACTION_WIDTH = 820;
type PdfSignaturePanelMode = 'digital' | 'visual';

interface PdfZoomControlProps {
  zoom: number;
  open: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
  labels: {
    zoom: string;
    resetZoom: string;
    fitWidth: string;
    fitHeight: string;
  };
  onToggle: () => void;
  onZoom: (value: number) => void;
  onFit: (mode: 'width' | 'height') => void;
}

/** The zoom value and its popover: slider, reset and fit to width or height. */
const PdfZoomControl = ({
  zoom,
  open,
  anchorRef,
  labels,
  onToggle,
  onZoom,
  onFit,
}: PdfZoomControlProps) => (
  <div className={styles.titlebarZoomPopoverAnchor} ref={anchorRef}>
    <button
      type="button"
      className={styles.titlebarZoomActionButton}
      onClick={onToggle}
      aria-expanded={open}
      aria-label={labels.zoom}
      title={labels.zoom}
    >
      {zoom}%
    </button>
    {open ? (
      <div className={styles.titlebarZoomPopover}>
        <input
          type="range"
          min={65}
          max={250}
          step={1}
          value={zoom}
          onChange={event => onZoom(Number(event.target.value))}
          aria-label={labels.zoom}
          title={labels.zoom}
          className={styles.titlebarZoomSlider}
        />
        <button
          type="button"
          className={styles.titlebarZoomValue}
          onClick={() => onZoom(100)}
          aria-label={labels.resetZoom}
          title={labels.resetZoom}
        >
          {zoom}%
        </button>
        <IconButton
          className={styles.titlebarIconButton}
          icon={<AutoHeightIcon style={{ transform: 'rotate(90deg)' }} />}
          onClick={() => onFit('width')}
          aria-label={labels.fitWidth}
          title={labels.fitWidth}
          tooltip={labels.fitWidth}
        />
        <IconButton
          className={styles.titlebarIconButton}
          icon={<AutoHeightIcon />}
          onClick={() => onFit('height')}
          aria-label={labels.fitHeight}
          title={labels.fitHeight}
          tooltip={labels.fitHeight}
        />
      </div>
    ) : null}
  </div>
);

interface PdfSearchControlProps {
  query: string;
  count: string;
  notice: string | null;
  labels: {
    searchInput: string;
    searchPrevious: string;
    searchNext: string;
    searchClose: string;
  };
  onQuery: (query: string) => void;
  onAction: (action: 'previous' | 'next' | 'close') => void;
}

/** The open search: its field, the match count and stepping through the matches. */
const PdfSearchControl = ({
  query,
  count,
  notice,
  labels,
  onQuery,
  onAction,
}: PdfSearchControlProps) => (
  <div className={styles.titlebarSearchControl} role="search">
    <SearchIcon />
    <input
      autoFocus
      value={query}
      onChange={event => onQuery(event.target.value)}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onAction('close');
          return;
        }
        if (event.key !== 'Enter') return;
        event.preventDefault();
        onAction(event.shiftKey ? 'previous' : 'next');
      }}
      aria-label={labels.searchInput}
      title={labels.searchInput}
      className={styles.titlebarSearchInput}
    />
    <span className={styles.titlebarSearchCount}>{count}</span>
    {notice ? (
      <span className={styles.titlebarSearchNotice} title={notice}>
        {notice}
      </span>
    ) : null}
    <IconButton
      className={styles.titlebarIconButton}
      icon={<ArrowUpSmallIcon />}
      onClick={() => onAction('previous')}
      aria-label={labels.searchPrevious}
      title={labels.searchPrevious}
      tooltip={labels.searchPrevious}
    ></IconButton>
    <IconButton
      className={styles.titlebarIconButton}
      icon={<ArrowDownSmallIcon />}
      onClick={() => onAction('next')}
      aria-label={labels.searchNext}
      title={labels.searchNext}
      tooltip={labels.searchNext}
    ></IconButton>
    <IconButton
      className={styles.titlebarIconButton}
      icon={<CloseIcon />}
      onClick={() => onAction('close')}
      aria-label={labels.searchClose}
      title={labels.searchClose}
      tooltip={labels.searchClose}
    ></IconButton>
  </div>
);

export interface TitlebarProps {
  source: AttachmentViewerSource;
  name: string;
  ext: string;
  size: string;
  zoom?: number;
  onClose?: () => void;
  hideCloseButton?: boolean;
  textEditorState?: AttachmentTextEditorState | null;
}

export const Titlebar = ({
  source,
  name,
  ext,
  size,
  zoom = 100,
  onClose,
  hideCloseButton,
  textEditorState,
}: TitlebarProps) => {
  const isPdf = ext.toLowerCase() === 'pdf';
  const pdfEventId = source.id;
  const [pdfZoom, setPdfZoom] = useState(zoom);
  const [pdfSearchOpen, setPdfSearchOpen] = useState(false);
  const [pdfSearchQuery, setPdfSearchQuery] = useState('');
  const [pdfSearchCount, setPdfSearchCount] = useState('0/0');
  const [pdfSearchNotice, setPdfSearchNotice] = useState<string | null>(null);
  const [pdfSignatureAvailable, setPdfSignatureAvailable] = useState(false);
  const [pdfSignatureOpen, setPdfSignatureOpen] = useState(false);
  const [pdfSignatureMode, setPdfSignatureMode] =
    useState<PdfSignaturePanelMode>('visual');
  const [pdfDigitalSigning, setPdfDigitalSigning] = useState(false);
  const [pdfSignatureLocked, setPdfSignatureLocked] = useState(false);
  const [pdfMailAvailable, setPdfMailAvailable] = useState(false);
  const [pdfComments, setPdfComments] = useState({
    available: false,
    open: false,
  });
  const [pdfSidebarWidth, setPdfSidebarWidth] = useState(0);
  const [pdfZoomOpen, setPdfZoomOpen] = useState(false);
  const [pdfActionMenuOpen, setPdfActionMenuOpen] = useState(false);
  const [pdfActionMenuCompact, setPdfActionMenuCompact] = useState(false);
  const [pdfReaderAreaWidth, setPdfReaderAreaWidth] = useState(0);
  const { openPromptModal, closePromptModal } = usePromptModal();
  const pdfActionMenuId = useId();
  const titlebarRef = useRef<HTMLDivElement | null>(null);
  const pdfZoomAnchorRef = useRef<HTMLDivElement | null>(null);
  const pdfActionMenuAnchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isPdf) return;
    const onZoomChange = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId?: string; zoom?: number }>)
        .detail;
      if (detail?.blockId !== pdfEventId || typeof detail.zoom !== 'number') {
        return;
      }
      setPdfZoom(Math.round(detail.zoom * 100));
    };
    window.addEventListener('streamlain:pdf-zoom-changed', onZoomChange);
    const onLayoutChange = (event: Event) => {
      const detail = (
        event as CustomEvent<{ blockId?: string; sidebarWidth?: number }>
      ).detail;
      if (detail?.blockId !== pdfEventId) return;
      setPdfSidebarWidth(
        typeof detail.sidebarWidth === 'number' ? detail.sidebarWidth : 0
      );
    };
    window.addEventListener('streamlain:pdf-layout-changed', onLayoutChange);
    const onFindStateChange = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          blockId?: string;
          open?: boolean;
          query?: string;
          countLabel?: string;
          noticeLabel?: string | null;
        }>
      ).detail;
      if (detail?.blockId !== pdfEventId) return;
      setPdfSearchOpen(!!detail.open);
      setPdfSearchQuery(detail.query ?? '');
      setPdfSearchCount(detail.countLabel ?? '0/0');
      setPdfSearchNotice(detail.noticeLabel ?? null);
    };
    window.addEventListener(
      'streamlain:pdf-find-state-changed',
      onFindStateChange
    );
    const onSignatureStateChange = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          blockId?: string;
          available?: boolean;
          digital?: boolean;
          lockedReason?: 'signed_copy';
          mode?: PdfSignaturePanelMode;
          open?: boolean;
        }>
      ).detail;
      if (detail?.blockId !== pdfEventId) return;
      setPdfSignatureAvailable(!!detail.available);
      setPdfDigitalSigning(!!detail.available && !!detail.digital);
      setPdfSignatureLocked(detail.lockedReason === 'signed_copy');
      setPdfSignatureOpen(!!detail.open);
      if (detail.mode === 'digital' || detail.mode === 'visual') {
        setPdfSignatureMode(detail.mode);
      }
    };
    window.addEventListener(
      'streamlain:pdf-signature-state-changed',
      onSignatureStateChange
    );
    const onMailStateChange = (event: Event) => {
      const detail = (
        event as CustomEvent<{ blockId?: string; available?: boolean }>
      ).detail;
      if (detail?.blockId !== pdfEventId) return;
      setPdfMailAvailable(!!detail.available);
    };
    window.addEventListener(
      'streamlain:pdf-mail-state-changed',
      onMailStateChange
    );
    const onCommentsStateChange = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          blockId?: string;
          available?: boolean;
          open?: boolean;
        }>
      ).detail;
      if (detail?.blockId !== pdfEventId) return;
      setPdfComments({ available: !!detail.available, open: !!detail.open });
    };
    window.addEventListener(
      'streamlain:pdf-comments-state-changed',
      onCommentsStateChange
    );
    return () => {
      window.removeEventListener(
        'streamlain:pdf-comments-state-changed',
        onCommentsStateChange
      );
      window.removeEventListener(
        'streamlain:pdf-mail-state-changed',
        onMailStateChange
      );
      window.removeEventListener('streamlain:pdf-zoom-changed', onZoomChange);
      window.removeEventListener(
        'streamlain:pdf-layout-changed',
        onLayoutChange
      );
      window.removeEventListener(
        'streamlain:pdf-find-state-changed',
        onFindStateChange
      );
      window.removeEventListener(
        'streamlain:pdf-signature-state-changed',
        onSignatureStateChange
      );
    };
  }, [isPdf, pdfEventId]);

  useEffect(() => {
    if (isPdf) return;
    setPdfSearchOpen(false);
    setPdfSearchQuery('');
    setPdfSearchCount('0/0');
    setPdfSearchNotice(null);
    setPdfSignatureAvailable(false);
    setPdfMailAvailable(false);
    setPdfSignatureOpen(false);
    setPdfSignatureMode('visual');
    setPdfSidebarWidth(0);
    setPdfZoomOpen(false);
    setPdfActionMenuOpen(false);
    setPdfActionMenuCompact(false);
    setPdfReaderAreaWidth(0);
  }, [isPdf]);

  useEffect(() => {
    if (!isPdf) {
      setPdfActionMenuCompact(false);
      return;
    }

    const titlebar = titlebarRef.current;
    if (!titlebar) return;

    let animationFrameId: number | null = null;
    const updateCompactState = () => {
      animationFrameId = null;
      const titlebarWidth = Math.round(titlebar.getBoundingClientRect().width);
      const readerAreaWidth = Math.max(
        0,
        titlebarWidth - Math.round(pdfSidebarWidth)
      );
      const nextCompact =
        readerAreaWidth > 0 &&
        readerAreaWidth < PDF_TITLEBAR_COMPACT_ACTION_WIDTH;
      setPdfReaderAreaWidth(current =>
        current === readerAreaWidth ? current : readerAreaWidth
      );
      setPdfActionMenuCompact(current =>
        current === nextCompact ? current : nextCompact
      );
      if (!nextCompact) {
        setPdfActionMenuOpen(false);
      }
    };
    const scheduleUpdate = () => {
      if (animationFrameId !== null) return;
      animationFrameId = window.requestAnimationFrame(updateCompactState);
    };

    updateCompactState();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleUpdate);
      return () => {
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }
        window.removeEventListener('resize', scheduleUpdate);
      };
    }

    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(titlebar);
    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      observer.disconnect();
    };
  }, [isPdf, pdfSidebarWidth]);

  useEffect(() => {
    if (!pdfZoomOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        pdfZoomAnchorRef.current?.contains(event.target)
      ) {
        return;
      }
      setPdfZoomOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPdfZoomOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [pdfZoomOpen]);

  useEffect(() => {
    if (!pdfActionMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        pdfActionMenuAnchorRef.current?.contains(event.target)
      ) {
        return;
      }
      setPdfActionMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPdfActionMenuOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [pdfActionMenuOpen]);

  const pdfToolsStyle = {
    '--pdf-sidebar-width': `${pdfSidebarWidth}px`,
  } as CSSProperties;

  const titlebarStyle = {
    '--pdf-reader-area-width': `${pdfReaderAreaWidth}px`,
  } as CSSProperties;

  const labels = {
    close: I18n.t('com.streamlain.pdf.viewer.close'),
    download: I18n.t('com.streamlain.pdf.viewer.download'),
    fitHeight: I18n.t('com.streamlain.pdf.viewer.fitHeight'),
    fitWidth: I18n.t('com.streamlain.pdf.viewer.fitWidth'),
    moreActions: I18n.t('com.streamlain.pdf.viewer.moreActions'),
    openInNewTab: I18n.t('com.streamlain.pdf.viewer.openInNewTab'),
    print: I18n.t('com.streamlain.pdf.viewer.print'),
    sendAsMail: I18n.t('com.streamlain.pdf.viewer.sendAsMail'),
    comments: I18n.t('com.streamlain.rightSidebar.comments'),
    rename: I18n.t('Rename'),
    resetZoom: I18n.t('com.streamlain.pdf.viewer.resetZoom'),
    search: I18n.t('com.streamlain.pdf.find.open'),
    searchClose: I18n.t('com.streamlain.pdf.find.close'),
    searchInput: I18n.t('com.streamlain.pdf.find.input'),
    searchNext: I18n.t('com.streamlain.pdf.find.next'),
    searchPrevious: I18n.t('com.streamlain.pdf.find.previous'),
    save: I18n.t('Save'),
    saved: I18n.t('Saved'),
    saving: I18n.t('Saving'),
    signedCopyLocked: getSignedPdfCopyLockedLabel(),
    digitalSignature: I18n.t('com.streamlain.pdf.signature.digital.open'),
    insertSignature: I18n.t('com.streamlain.pdf.signature.visual.open'),
    signatures: I18n.t('com.streamlain.pdf.signature.open'),
    fileNameEmpty: I18n.t('com.streamlain.blob.rename.empty'),
    zoom: I18n.t('com.streamlain.pdf.viewer.zoom'),
    zoomIn: I18n.t('Zoom in'),
    zoomOut: I18n.t('Zoom out'),
  };
  const canRenameSource = Boolean(source.rename) && source.canRename !== false;

  const setPdfViewerZoom = (value: number) => {
    setPdfZoom(value);
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-zoom-set', {
        detail: { blockId: pdfEventId, zoom: value / 100 },
      })
    );
  };

  const dispatchPdfZoomFit = (mode: 'width' | 'height') => {
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-zoom-fit', {
        detail: { blockId: pdfEventId, mode },
      })
    );
  };

  const openPdfSearch = () => {
    if (!isPdf) return;
    setPdfZoomOpen(false);
    setPdfActionMenuOpen(false);
    setPdfSearchOpen(true);
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-find-open', {
        detail: { blockId: pdfEventId },
      })
    );
  };

  const openPdfSignatures = (mode: PdfSignaturePanelMode) => {
    if (!isPdf || !pdfSignatureAvailable) return;
    if (mode === 'digital' && !pdfDigitalSigning) return;
    setPdfZoomOpen(false);
    setPdfActionMenuOpen(false);
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-signature-toggle', {
        detail: { blockId: pdfEventId, mode },
      })
    );
  };

  const updatePdfSearchQuery = (query: string) => {
    setPdfSearchQuery(query);
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-find-query', {
        detail: { blockId: pdfEventId, query },
      })
    );
  };

  const dispatchPdfFindAction = (action: 'previous' | 'next' | 'close') => {
    if (action === 'close') {
      setPdfSearchOpen(false);
    }
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-find-action', {
        detail: { blockId: pdfEventId, action },
      })
    );
  };

  const downloadSource = () => {
    setPdfActionMenuOpen(false);
    if (isPdf) {
      window.dispatchEvent(
        new CustomEvent('streamlain:pdf-download-request', {
          detail: { blockId: pdfEventId },
        })
      );
      return;
    }
    download(source).catch(console.error);
  };

  // The viewer builds the file and hands it to a new message in the mail view.
  const sendPdfAsMail = () => {
    setPdfActionMenuOpen(false);
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-send-mail-request', {
        detail: { blockId: pdfEventId },
      })
    );
  };

  // The viewer shows the doc's comments in a column beside the page.
  const togglePdfComments = () => {
    setPdfActionMenuOpen(false);
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-comments-toggle', {
        detail: { blockId: pdfEventId },
      })
    );
  };

  const renameSource = () => {
    if (!canRenameSource) {
      return;
    }
    setPdfActionMenuOpen(false);
    const { editableName, extension } = splitBlobNameForRename(source.name);
    openPromptModal(
      {
        title: labels.rename,
        label: extension ? `${labels.rename} ${extension}` : labels.rename,
        defaultValue: editableName,
        confirmText: labels.rename,
        cancelText: I18n.t('Cancel'),
        inputOptions: {
          autoSelect: true,
        },
        onConfirm: text => {
          const nextName = composeBlobNameFromRename(text, extension);
          if (!nextName) {
            notify.error({ title: labels.fileNameEmpty });
            return;
          }
          source.rename?.(nextName);
          closePromptModal();
        },
      },
      { autoClose: false }
    );
  };

  const requestPdfPrint = () => {
    setPdfActionMenuOpen(false);
    window.dispatchEvent(
      new CustomEvent('streamlain:pdf-print-request', {
        detail: { blockId: pdfEventId },
      })
    );
  };

  const openPdfInNewTab = () => {
    setPdfActionMenuOpen(false);
    window.open(window.location.href, '_blank', 'noopener,noreferrer');
  };

  const zoomControl = (
    <PdfZoomControl
      zoom={pdfZoom}
      open={pdfZoomOpen}
      anchorRef={pdfZoomAnchorRef}
      labels={labels}
      onToggle={() => {
        setPdfActionMenuOpen(false);
        setPdfZoomOpen(open => !open);
      }}
      onZoom={setPdfViewerZoom}
      onFit={dispatchPdfZoomFit}
    />
  );
  const searchControl = (
    <PdfSearchControl
      query={pdfSearchQuery}
      count={pdfSearchCount}
      notice={pdfSearchNotice}
      labels={labels}
      onQuery={updatePdfSearchQuery}
      onAction={dispatchPdfFindAction}
    />
  );
  return (
    <div
      ref={titlebarRef}
      className={styles.titlebar}
      data-testid="attachment-viewer-titlebar"
      data-pdf={isPdf ? 'true' : undefined}
      data-compact-actions={isPdf && pdfActionMenuCompact ? 'true' : undefined}
      style={titlebarStyle}
    >
      <div className={styles.titlebarMeta}>
        <div className={styles.titlebarFileIdentity}>
          <div className={styles.titlebarName}>
            <div>{name}</div>
            <span>.{ext}</span>
          </div>
          <div className={styles.titlebarSize}>{size}</div>
        </div>
      </div>

      {isPdf ? (
        <div
          className={styles.titlebarPdfTools}
          data-search-open={pdfSearchOpen ? 'true' : undefined}
          style={pdfToolsStyle}
        >
          {pdfSearchOpen ? searchControl : null}
        </div>
      ) : (
        <div className={styles.titlebarPdfTools} />
      )}

      <div className={styles.titlebarActions}>
        {isPdf && !pdfSearchOpen ? (
          <>
            {zoomControl}
            <IconButton
              className={clsx(
                styles.titlebarIconButton,
                styles.titlebarSecondaryAction
              )}
              icon={<DownloadIcon />}
              onClick={downloadSource}
              aria-label={labels.download}
              title={labels.download}
              tooltip={labels.download}
            />
            <IconButton
              className={styles.titlebarIconButton}
              icon={<SearchIcon />}
              onClick={openPdfSearch}
              aria-label={labels.search}
              title={labels.search}
              tooltip={labels.search}
            />
            {pdfSignatureLocked ? (
              <>
                {/* A signed copy keeps its signing tools in sight and says why they rest. */}
                <IconButton
                  className={clsx(
                    styles.titlebarIconButton,
                    styles.titlebarSecondaryAction
                  )}
                  icon={<EditIcon />}
                  aria-disabled="true"
                  data-locked="true"
                  data-testid="pdf-titlebar-insert-signature"
                  aria-label={`${labels.insertSignature}. ${labels.signedCopyLocked}`}
                  title={labels.signedCopyLocked}
                  tooltip={labels.signedCopyLocked}
                />
                <IconButton
                  className={clsx(
                    styles.titlebarIconButton,
                    styles.titlebarSecondaryAction
                  )}
                  icon={<ReauthenticationIcon />}
                  aria-disabled="true"
                  data-locked="true"
                  data-testid="pdf-titlebar-digital-signature"
                  aria-label={`${labels.digitalSignature}. ${labels.signedCopyLocked}`}
                  title={labels.signedCopyLocked}
                  tooltip={labels.signedCopyLocked}
                />
              </>
            ) : null}
            {pdfSignatureAvailable ? (
              <IconButton
                className={clsx(
                  styles.titlebarIconButton,
                  styles.titlebarSecondaryAction
                )}
                icon={<EditIcon />}
                onClick={() => openPdfSignatures('visual')}
                aria-expanded={
                  pdfSignatureOpen && pdfSignatureMode === 'visual'
                }
                data-testid="pdf-titlebar-insert-signature"
                aria-label={labels.insertSignature}
                title={labels.insertSignature}
                tooltip={labels.insertSignature}
              />
            ) : null}
            {pdfDigitalSigning ? (
              <IconButton
                className={clsx(
                  styles.titlebarIconButton,
                  styles.titlebarSecondaryAction
                )}
                icon={<ReauthenticationIcon />}
                onClick={() => openPdfSignatures('digital')}
                aria-expanded={
                  pdfSignatureOpen && pdfSignatureMode === 'digital'
                }
                data-testid="pdf-titlebar-digital-signature"
                aria-label={labels.digitalSignature}
                title={labels.digitalSignature}
                tooltip={labels.digitalSignature}
              />
            ) : null}
            <IconButton
              className={clsx(
                styles.titlebarIconButton,
                styles.titlebarSecondaryAction
              )}
              icon={<PrinterIcon />}
              onClick={requestPdfPrint}
              aria-label={labels.print}
              title={labels.print}
              tooltip={labels.print}
            />
            {pdfComments.available ? (
              <IconButton
                className={clsx(
                  styles.titlebarIconButton,
                  styles.titlebarSecondaryAction
                )}
                icon={<CommentIcon />}
                onClick={togglePdfComments}
                aria-expanded={pdfComments.open}
                data-active={pdfComments.open ? 'true' : undefined}
                aria-label={labels.comments}
                title={labels.comments}
                tooltip={labels.comments}
                data-testid="pdf-titlebar-comments"
              />
            ) : null}
            {pdfMailAvailable ? (
              <IconButton
                className={clsx(
                  styles.titlebarIconButton,
                  styles.titlebarSecondaryAction
                )}
                icon={<EmailIcon />}
                onClick={sendPdfAsMail}
                aria-label={labels.sendAsMail}
                title={labels.sendAsMail}
                tooltip={labels.sendAsMail}
                data-testid="pdf-titlebar-send-mail"
              />
            ) : null}
            <div
              className={styles.titlebarActionMenuAnchor}
              ref={pdfActionMenuAnchorRef}
            >
              <IconButton
                className={styles.titlebarMoreButton}
                icon={<MoreHorizontalIcon />}
                onClick={() => {
                  setPdfZoomOpen(false);
                  setPdfActionMenuOpen(open => !open);
                }}
                aria-controls={pdfActionMenuId}
                aria-expanded={pdfActionMenuOpen}
                aria-label={labels.moreActions}
                title={labels.moreActions}
                tooltip={labels.moreActions}
              />
              <div
                className={styles.titlebarActionMenu}
                data-expanded={pdfActionMenuOpen ? 'true' : 'false'}
                id={pdfActionMenuId}
                role="menu"
                aria-hidden={!pdfActionMenuOpen}
              >
                {pdfSignatureAvailable ? (
                  <button
                    type="button"
                    className={styles.titlebarActionMenuItem}
                    data-active={
                      pdfSignatureOpen && pdfSignatureMode === 'visual'
                        ? 'true'
                        : undefined
                    }
                    onClick={() => openPdfSignatures('visual')}
                    role="menuitem"
                    tabIndex={pdfActionMenuOpen ? 0 : -1}
                    title={labels.insertSignature}
                  >
                    <EditIcon />
                    <span>{labels.insertSignature}</span>
                  </button>
                ) : null}
                {pdfDigitalSigning ? (
                  <button
                    type="button"
                    className={styles.titlebarActionMenuItem}
                    data-active={
                      pdfSignatureOpen && pdfSignatureMode === 'digital'
                        ? 'true'
                        : undefined
                    }
                    onClick={() => openPdfSignatures('digital')}
                    role="menuitem"
                    tabIndex={pdfActionMenuOpen ? 0 : -1}
                    title={labels.digitalSignature}
                  >
                    <ReauthenticationIcon />
                    <span>{labels.digitalSignature}</span>
                  </button>
                ) : null}
                {pdfSignatureLocked ? (
                  <>
                    <button
                      type="button"
                      className={styles.titlebarActionMenuItem}
                      aria-disabled="true"
                      data-locked="true"
                      role="menuitem"
                      tabIndex={pdfActionMenuOpen ? 0 : -1}
                      title={labels.signedCopyLocked}
                    >
                      <EditIcon />
                      <span>{labels.insertSignature}</span>
                    </button>
                    <button
                      type="button"
                      className={styles.titlebarActionMenuItem}
                      aria-disabled="true"
                      data-locked="true"
                      role="menuitem"
                      tabIndex={pdfActionMenuOpen ? 0 : -1}
                      title={labels.signedCopyLocked}
                    >
                      <ReauthenticationIcon />
                      <span>{labels.digitalSignature}</span>
                    </button>
                  </>
                ) : null}
                {canRenameSource ? (
                  <button
                    type="button"
                    className={styles.titlebarActionMenuItem}
                    onClick={renameSource}
                    role="menuitem"
                    tabIndex={pdfActionMenuOpen ? 0 : -1}
                    title={labels.rename}
                  >
                    <EditIcon />
                    <span>{labels.rename}</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.titlebarActionMenuItem}
                  onClick={downloadSource}
                  role="menuitem"
                  tabIndex={pdfActionMenuOpen ? 0 : -1}
                  title={labels.download}
                >
                  <DownloadIcon />
                  <span>{labels.download}</span>
                </button>
                <button
                  type="button"
                  className={styles.titlebarActionMenuItem}
                  onClick={requestPdfPrint}
                  role="menuitem"
                  tabIndex={pdfActionMenuOpen ? 0 : -1}
                  title={labels.print}
                >
                  <PrinterIcon />
                  <span>{labels.print}</span>
                </button>
                {pdfComments.available ? (
                  <button
                    type="button"
                    className={styles.titlebarActionMenuItem}
                    data-active={pdfComments.open ? 'true' : undefined}
                    onClick={togglePdfComments}
                    role="menuitem"
                    tabIndex={pdfActionMenuOpen ? 0 : -1}
                    title={labels.comments}
                    data-testid="pdf-titlebar-menu-comments"
                  >
                    <CommentIcon />
                    <span>{labels.comments}</span>
                  </button>
                ) : null}
                {pdfMailAvailable ? (
                  <button
                    type="button"
                    className={styles.titlebarActionMenuItem}
                    onClick={sendPdfAsMail}
                    role="menuitem"
                    tabIndex={pdfActionMenuOpen ? 0 : -1}
                    title={labels.sendAsMail}
                    data-testid="pdf-titlebar-menu-send-mail"
                  >
                    <EmailIcon />
                    <span>{labels.sendAsMail}</span>
                  </button>
                ) : null}
                {onClose && !hideCloseButton ? (
                  <button
                    type="button"
                    className={styles.titlebarActionMenuItem}
                    onClick={openPdfInNewTab}
                    role="menuitem"
                    tabIndex={pdfActionMenuOpen ? 0 : -1}
                    title={labels.openInNewTab}
                  >
                    <OpenInNewIcon />
                    <span>{labels.openInNewTab}</span>
                  </button>
                ) : null}
              </div>
            </div>
          </>
        ) : !isPdf ? (
          <>
            {textEditorState?.canEdit ? (
              <IconButton
                className={styles.titlebarSaveButton}
                iconClassName={styles.titlebarSaveIcon}
                data-dirty={textEditorState.dirty ? 'true' : 'false'}
                icon={<SaveIcon />}
                disabled={!textEditorState.dirty || textEditorState.saving}
                onClick={() => {
                  void textEditorState.save();
                }}
                aria-label={
                  textEditorState.saving
                    ? labels.saving
                    : textEditorState.dirty
                      ? labels.save
                      : labels.saved
                }
                title={
                  textEditorState.saving
                    ? labels.saving
                    : textEditorState.dirty
                      ? labels.save
                      : labels.saved
                }
                tooltip={
                  textEditorState.saving
                    ? labels.saving
                    : textEditorState.dirty
                      ? labels.save
                      : labels.saved
                }
              />
            ) : null}
            {canRenameSource ? (
              <IconButton
                className={styles.titlebarIconButton}
                icon={<EditIcon />}
                onClick={renameSource}
                aria-label={labels.rename}
                title={labels.rename}
                tooltip={labels.rename}
              />
            ) : null}
            <IconButton
              className={styles.titlebarIconButton}
              icon={<DownloadIcon />}
              onClick={downloadSource}
              aria-label={labels.download}
              title={labels.download}
              tooltip={labels.download}
            />
          </>
        ) : null}
        {onClose && !hideCloseButton ? (
          <>
            {isPdf ? (
              <IconButton
                className={clsx(
                  styles.titlebarIconButton,
                  styles.titlebarSecondaryAction
                )}
                icon={<OpenInNewIcon />}
                onClick={openPdfInNewTab}
                aria-label={labels.openInNewTab}
                title={labels.openInNewTab}
                tooltip={labels.openInNewTab}
              ></IconButton>
            ) : null}
            <IconButton
              className={styles.titlebarCloseButton}
              icon={<CloseIcon />}
              onClick={onClose}
              aria-label={labels.close}
              title={labels.close}
              tooltip={labels.close}
            ></IconButton>
          </>
        ) : !onClose ? (
          <div
            className={clsx([
              styles.titlebarLegacyZoom,
              'zoom',
              {
                show: false,
              },
            ])}
          >
            <IconButton
              icon={<ZoomDownIcon />}
              aria-label={labels.zoomOut}
              title={labels.zoomOut}
              tooltip={labels.zoomOut}
            ></IconButton>
            <div>{zoom}%</div>
            <IconButton
              icon={<ZoomUpIcon />}
              aria-label={labels.zoomIn}
              title={labels.zoomIn}
              tooltip={labels.zoomIn}
            ></IconButton>
          </div>
        ) : null}
      </div>
    </div>
  );
};
